import React, { useState, useEffect, useMemo } from 'react';
import { dataService } from '../services/dataService';

import ExecutionView from './ExecutionView';
import AssetKennisKluis from './AssetKennisKluis';
import { enrichPermitWithLinks } from '../utils/linkEnricher';
import { PdfService } from '../services/PdfService';

const AssetDashboard = ({
    assetId,
    instanceName,
    photoUrl,
    deepLinkLocation,
    location,
    hierarchyData,
    globalLibrary,
    taskMetadata,
    componentMetadata,
    currentUser,
    onBack,
    onHome,
    onStartWizard
}) => {
    console.log("AssetDashboard: Rendering", {
        assetId,
        instanceName,
        deepLinkLocation,
        location,
        hierarchyData,
        hasGlobalLibrary: !!globalLibrary,
        hasTaskMetadata: !!taskMetadata
    });

    const [permits, setPermits] = useState([]); // Active
    const [historyPermits, setHistoryPermits] = useState([]); // Logbook (Completed)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPermit, setSelectedPermit] = useState(null);
    const [users, setUsers] = useState([]);

    // UI State
    const [dashboardTab, setDashboardTab] = useState('active'); // 'active' | 'logbook' | 'ai'

    useEffect(() => {
        const loadUsers = async () => {
            try {
                const allUsers = await dataService.getAllUsers();
                setUsers(allUsers || []);
            } catch (err) {
                console.error("Failed to load users for assignment:", err);
            }
        };
        loadUsers();
    }, []);

    useEffect(() => {
        const loadPermits = async () => {
            if (!assetId) return;
            setLoading(true);
            try {
                // 1. Fetch Active Permits
                const activePermits = await dataService.getPermitsByComponent(location, assetId);
                const filteredActive = activePermits.filter(p => !instanceName || p.instanceName === instanceName);
                setPermits(filteredActive);

                // 2. Fetch History (Logbook) if we have precise location
                if (deepLinkLocation && instanceName) {
                    const historyRaw = await dataService.getPermitsByInstance(
                        location,
                        deepLinkLocation.hall,
                        deepLinkLocation.line,
                        deepLinkLocation.subline,
                        instanceName
                    );
                    // Filter for completed only (or all history?)
                    // Usually logbook implies finished work. Active work is in the active tab.
                    // But getPermitsByInstance returns ALL.
                    // Let's filter: Status == 'completed' OR 'rejected'
                    const historyData = historyRaw.filter(p => p.status === 'completed' || p.status === 'rejected');
                    setHistoryPermits(historyData.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)));
                }

            } catch (err) {
                console.error("Error loading permits:", err);
                setError("Kon werkplannen niet laden.");
            } finally {
                setLoading(false);
            }
        };

        loadPermits();
    }, [assetId, location, instanceName, deepLinkLocation]);

    const handleExportPdf = () => {
        const assetData = {
            assetId,
            instanceName,
            locationString: locationDisplay,
            mjop: hierarchyData?.[deepLinkLocation?.hall]?.[deepLinkLocation?.line]?.[deepLinkLocation?.subline]?._componentData?.[assetId]?.[instanceName] || hierarchyData?.[deepLinkLocation?.hall]?.[deepLinkLocation?.line]?.[deepLinkLocation?.subline]?._instances?.find(i => i.name === instanceName)
        };

        // Combine history for report? Or just history?
        // User asked for "overzicht".
        // Let's pass all historyPermits.
        PdfService.generateAssetReport(assetData, historyPermits);
    };

    // Get Metadata for documentation
    const meta = taskMetadata?.[assetId] || {};

    // Components have tasks defined in globalLibrary (onderdeel_tasks)
    const componentTasks = globalLibrary?.[assetId] || [];

    // -------------------------------------------------------------------------
    // SOP LOGIC: SPLIT ACTIVE VS. LIBRARY
    // -------------------------------------------------------------------------

    // 1. Identify active tasks (from permits)
    const activeTaskNames = permits.map(p => p.task || p.taskName).filter(Boolean);

    // 2. Helper to extract unique SOPs from a list of tasks
    const extractSopsFromTasks = (taskList) => {
        return taskList.reduce((acc, taskName) => {
            const taskMeta = taskMetadata?.[taskName];
            if (taskMeta?.sopLinks) {
                taskMeta.sopLinks.forEach(sop => {
                    const hasAccess = !sop.allowedLocations || sop.allowedLocations.length === 0 || (location && sop.allowedLocations.includes(location));
                    if (hasAccess && sop.name && sop.url) {
                        if (!acc.some(s => s.url === sop.url)) { // Dedup by URL
                            acc.push({ ...sop, taskName });
                        }
                    }
                });
            }
            return acc;
        }, []);
    };

    // 3. Get SOPs from ACTIVE permits
    // We base this on `activeTaskNames` which comes from the `permits` state
    const activeSops = extractSopsFromTasks(activeTaskNames);

    // 4. Get SOPs from LIBRARY (General)
    // We base this on `componentTasks` (all tasks for this asset type)
    // AND we exclude SOPs that are already in the "active" list to avoid duplication
    const librarySopsRaw = extractSopsFromTasks(componentTasks);
    // Exclude duplicates
    const librarySops = librarySopsRaw.filter(libSop => !activeSops.some(actSop => actSop.url === libSop.url));
    const allSops = [...activeSops, ...librarySops]; // For legacy reference if needed? No, we use separate lists.

    // 5. Instance-level links (prioritized over type-level links)
    const instanceLinks = useMemo(() => {
        if (!hierarchyData || !deepLinkLocation || !instanceName) return null;

        const { hall, line, subline } = deepLinkLocation;
        const hallObj = hierarchyData[hall];
        if (!hallObj) return null;

        // Resolve Line
        const container = hallObj.lines || hallObj; // Nested or Flat
        const lineObj = container[line];
        if (!lineObj) return null;

        // Resolve Subline
        const subContainer = lineObj.sublines || lineObj;
        const sublineObj = subline ? subContainer[subline] : lineObj;

        if (!sublineObj) return null;

        const instances = sublineObj._instances || [];
        return instances.find(i => i.name === instanceName);
    }, [hierarchyData, deepLinkLocation, instanceName]);

    const finalCompMeta = useMemo(() => {
        const base = componentMetadata?.[assetId] || {};
        if (!instanceLinks) return base;

        return {
            ...base,
            // Prioritize instance links if they exist and are not empty
            docLink: (instanceLinks.docLink && instanceLinks.docLink.trim() !== '') ? instanceLinks.docLink : base.docLink,
            docLinkElectro: (instanceLinks.docLinkElectro && instanceLinks.docLinkElectro.trim() !== '') ? instanceLinks.docLinkElectro : base.docLinkElectro,
            docLinkManual: (instanceLinks.docLinkManual && instanceLinks.docLinkManual.trim() !== '') ? instanceLinks.docLinkManual : base.docLinkManual
        };
    }, [componentMetadata, assetId, instanceLinks]);

    // -------------------------------------------------------------------------

    const handlePermitClick = (permit) => {
        // Toggle: if already selected, deselect. Otherwise select.
        if (selectedPermit && selectedPermit.id === permit.id) {
            setSelectedPermit(null);
        } else {
            setSelectedPermit(permit);
        }
    };

    // Helper for location display
    const locationDisplay = deepLinkLocation
        ? `${location ? location + ' → ' : ''}${deepLinkLocation.hall} → ${deepLinkLocation.line}${deepLinkLocation.subline ? ' → ' + deepLinkLocation.subline : ''}`
        : location;

    const formatDate = (permit) => {
        if (!permit) return '...';
        if (permit.createdAt && typeof permit.createdAt === 'string') {
            // ISO String
            return new Date(permit.createdAt).toLocaleDateString();
        }
        if (permit.created_at?.seconds) {
            // Firestore Timestamp
            return new Date(permit.created_at.seconds * 1000).toLocaleDateString();
        }
        return 'Onbekend';
    };

    return (
        <div className="min-h-screen bg-slate-900 pb-20">
            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 p-2 sticky top-0 z-10 shadow-xl flex items-center justify-between">
                <button onClick={onBack} className="btn btn-ghost btn-sm text-slate-400 gap-2">
                    <span>←</span> Terug
                </button>
                <div className="flex items-center gap-2">
                    <div className="text-xs text-blue-500 font-bold uppercase tracking-wider mr-2">Asset Dashboard</div>
                    {onHome && (
                        <button onClick={onHome} className="btn btn-xs btn-ghost text-slate-400 hover:text-white">
                            🏠
                        </button>
                    )}
                </div>
            </div>

            {/* SPLIT LAYOUT CONTAINER */}
            <div className="flex flex-col md:flex-row w-full max-w-7xl mx-auto">

                {/* HERO IMAGE COLUMN */}
                {photoUrl && (
                    <div className="w-full md:w-1/2 bg-black relative md:sticky md:top-14 md:h-screen-minus-header">
                        <img
                            src={photoUrl}
                            alt="Asset"
                            className="w-full h-[40vh] md:h-full object-contain mx-auto"
                        />
                        <div className="absolute inset-0 ring-1 ring-inset ring-slate-700/50 pointer-events-none"></div>
                    </div>
                )}

                {/* CONTENT COLUMN */}
                <div className={`p-4 space-y-6 ${photoUrl ? 'w-full md:w-1/2' : 'max-w-lg mx-auto'}`}>

                    {/* INFO HEADER */}
                    <div className="text-center relative -mt-2">
                        <h1 className="text-2xl font-black text-white leading-tight mb-1">{instanceName || assetId}</h1>
                        {instanceName && instanceName !== assetId && (
                            <div className="text-sm text-blue-400 font-bold mb-2">Type: {assetId}</div>
                        )}
                        {locationDisplay && (
                            <div className="inline-block px-3 py-1 bg-slate-800 rounded-full border border-slate-700 text-xs text-slate-400">
                                📍 {locationDisplay}
                            </div>
                        )}
                    </div>

                    {/* --- MACHINE DOCUMENTATION (Promoted to Top) --- */}
                    {(() => {
                        if (!deepLinkLocation || !hierarchyData) return null;
                        const { hall, line, subline } = deepLinkLocation;
                        const sublineObj = hierarchyData[hall]?.[line]?.[subline];
                        if (!sublineObj) return null;

                        const docs = [];
                        if (sublineObj.docLink) docs.push({ type: 'tech', label: 'Technische Tekeningen', url: sublineObj.docLink, icon: '📐', color: '#0ea5e9' });
                        if (sublineObj.docLinkElectro) docs.push({ type: 'electro', label: 'Elektro Tekeningen', url: sublineObj.docLinkElectro, icon: '⚡', color: '#eab308' });
                        if (sublineObj.docLinkManual) docs.push({ type: 'manual', label: 'Handleidingen', url: sublineObj.docLinkManual, icon: '📘', color: '#38bdf8' });

                        if (docs.length === 0) return null;

                        return (
                            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-4 shadow-sm backdrop-blur-sm">
                                <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <span>📂 Machine Documentatie</span>
                                </h3>
                                <div className="grid grid-cols-1 gap-2">
                                    {docs.map((doc, idx) => (
                                        <a
                                            key={idx}
                                            href={doc.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-3 p-3 bg-slate-900/60 border border-slate-700 rounded-xl hover:bg-slate-800 hover:border-blue-500/30 transition-all group no-underline"
                                        >
                                            <span className="text-xl w-8 text-center">{doc.icon}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-sm truncate" style={{ color: doc.color }}>{doc.label}</div>
                                                <div className="text-[10px] text-slate-500">{subline}</div>
                                            </div>
                                            <span className="text-slate-600 group-hover:text-blue-400 transition-colors">↗</span>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* --- WORKFLOW SELECTION (3-FLOWS) --- */}
                    <div className="space-y-4">

                        {/* W1: YOUR ASSIGNED TASKS */}
                        {(() => {
                            const myTasks = permits.filter(p => (p.assignedTo === currentUser.uid || p.executorId === currentUser.uid) && p.status !== 'completed');
                            if (myTasks.length === 0) return null;
                            return (
                                <div className="card bg-blue-900/40 border border-blue-400/50 p-4 shadow-xl">
                                    <h2 className="text-sm font-black text-blue-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
                                        Uw Openstaande Taken (W1)
                                    </h2>
                                    <div className="space-y-3">
                                        {myTasks.map(task => (
                                            <button
                                                key={task.id}
                                                onClick={() => handlePermitClick(task)}
                                                className="w-full text-left bg-slate-900/80 p-3 rounded-lg border border-blue-500/30 hover:border-blue-400 transition-all group"
                                            >
                                                <div className="text-xs text-slate-500 mb-1">Order: {task.ultimoNr || 'P-ASSIGNED'}</div>
                                                <div className="font-bold text-white group-hover:text-blue-300">{task.task || task.taskName}</div>
                                                <div className="text-[10px] text-blue-400/80 mt-1 uppercase font-bold tracking-tighter">▶ Starten / Hervatten</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* W2 & W3 ACTION BOX */}
                        <div className="grid grid-cols-1 gap-3">

                            {/* W3: STORING VERHELPEN (Breakdown) - Authorized only */}
                            {currentUser.canCreatePlans && (
                                <button
                                    onClick={() => onStartWizard(assetId, { isBreakdown: true })}
                                    className="p-5 bg-red-600 hover:bg-red-700 text-white rounded-2xl border-none shadow-lg shadow-red-900/40 flex flex-col items-center justify-center transition-all active:scale-95 group overflow-hidden relative"
                                >
                                    <div className="flex items-center gap-3 mb-1">
                                        <span className="text-2xl group-hover:scale-125 transition-transform duration-300">🚨</span>
                                        <span className="text-lg font-black uppercase tracking-widest">Storing Verhelpen</span>
                                    </div>
                                    <span className="text-[10px] opacity-70 font-bold uppercase tracking-tight">Ongeplande Reparaties (W3)</span>
                                    <div className="absolute top-0 right-0 p-1 bg-white/10 text-[8px] font-black group-hover:bg-white/20">BREAKDOWN</div>
                                </button>
                            )}

                            {/* W2: STANDAARD WERKPLAN (Self-service) */}
                            <div className="bg-slate-800/80 rounded-2xl border border-slate-700 p-4">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center justify-between">
                                    <span>Beschikbare Werkplannen (W2)</span>
                                    <span className="text-slate-600">Gepland</span>
                                </h3>
                                <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
                                    {componentTasks.length > 0 ? componentTasks.map((taskName, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => onStartWizard(assetId, { task: taskName })}
                                            className="w-full text-left p-2.5 rounded-xl bg-slate-900 border border-slate-700 hover:border-blue-500/50 hover:bg-slate-800 transition-all text-sm group"
                                        >
                                            <div className="flex items-center justify-between text-slate-300 group-hover:text-blue-300">
                                                <span className="font-bold truncate pr-2">{taskName}</span>
                                                <span className="text-blue-500/50 opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                                            </div>
                                        </button>
                                    )) : (
                                        <div className="text-[10px] text-slate-600 italic">Geen standaard werkplannen gevonden voor dit type.</div>
                                    )}
                                </div>
                                <button
                                    onClick={() => onStartWizard(assetId)}
                                    className="w-full mt-4 py-2 border-2 border-dashed border-slate-700 rounded-xl text-slate-500 hover:text-white hover:border-slate-500 transition-all text-[10px] font-black uppercase tracking-widest"
                                >
                                    + Ander Werkplan Starten
                                </button>
                            </div>

                        </div>
                    </div>

                    {/* TAB NAVIGATION & EXPORT */}
                    <div className="flex items-center justify-between border-b border-slate-700 pb-3">
                        <div className="flex bg-slate-800 rounded-lg p-1 border border-slate-700/50">
                            <button
                                onClick={() => setDashboardTab('active')}
                                className={`btn btn-sm px-4 py-1.5 transition-all ${dashboardTab === 'active' ? 'bg-white text-slate-900 shadow-lg scale-105 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-700/50 border-none'}`}
                            >
                                Actief ({permits.length})
                            </button>
                            <button
                                onClick={() => setDashboardTab('logbook')}
                                className={`btn btn-sm px-4 py-1.5 transition-all ${dashboardTab === 'logbook' ? 'bg-white text-slate-900 shadow-lg scale-105 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-700/50 border-none'}`}
                            >
                                Logboek ({historyPermits.length})
                            </button>
                            <button
                                onClick={() => setDashboardTab('ai')}
                                className={`btn btn-sm px-4 py-1.5 transition-all ${dashboardTab === 'ai' ? 'bg-purple-600 text-white shadow-lg scale-105 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-700/50 border-none'}`}
                            >
                                🧠 AI
                            </button>
                        </div>

                        <button
                            onClick={handleExportPdf}
                            className="btn btn-sm bg-white text-slate-900 border-none shadow-md hover:bg-slate-100 font-bold gap-2"
                            title="Exporteer naar PDF"
                        >
                            <span>📄</span> Export
                        </button>
                    </div>

                    {/* SECTION 1: Active Permits */}
                    {dashboardTab === 'active' && (
                        <div className="animate-fade-in">
                            <h3 className="hidden">Actieve Werkplannen</h3> {/* Hidden for A11y but using Tabs now */}

                            {error && <div className="alert alert-error text-xs">{error}</div>}

                            {!loading && permits.length === 0 && (
                                <div className="text-center p-6 border-2 border-dashed border-slate-700 rounded-lg text-slate-500 italic text-sm">
                                    Geen actieve werkplannen op dit moment.
                                </div>
                            )}

                            <div className="space-y-3">
                                {permits.map(permit => (
                                    <div key={permit.id}
                                        onClick={() => handlePermitClick(permit)}
                                        style={{ cursor: 'pointer' }}
                                        className="card bg-slate-800 border-slate-700 p-3 hover:border-slate-500 transition-colors !cursor-pointer group hover:bg-slate-700"
                                    >
                                        <div className="flex justify-between items-start mb-1">
                                            <span className={`badge badge-sm font-bold ${permit.status === 'approved' ? 'badge-success text-white' :
                                                permit.status === 'active' ? 'badge-info text-white' :
                                                    permit.status === 'pending' ? 'badge-warning text-white' : 'badge-ghost'
                                                }`}>
                                                {permit.status === 'approved' ? 'GOEDGEKEURD' :
                                                    permit.status === 'active' ? 'IN UITVOERING' :
                                                        permit.status === 'pending' ? 'IN BEHANDELING' : permit.status}
                                            </span>
                                            <span className="text-xs text-slate-400 font-mono">{formatDate(permit)}</span>
                                        </div>
                                        <h4 className="font-bold text-white group-hover:text-blue-400 transition-colors">{permit.task || permit.taskName || 'Onbekende taak'}</h4>
                                        <div className="text-xs text-slate-400 mt-1">
                                            Uitvoerder: <span className="text-slate-300">{permit.executor || 'Onbekend'}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* SECTION 1B: Logbook (History) */}
                    {dashboardTab === 'logbook' && (
                        <div className="animate-fade-in">
                            <div className="overflow-x-auto rounded border border-slate-700">
                                <table className="table w-full text-xs">
                                    <thead className="bg-slate-800 text-slate-400">
                                        <tr>
                                            <th>Datum</th>
                                            <th>Taak</th>
                                            <th>Door</th>
                                            <th>Conditie</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyPermits.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="text-center text-slate-500 py-4 italic">Geen historie beschikbaar.</td>
                                            </tr>
                                        ) : (
                                            historyPermits.map(permit => (
                                                <tr key={permit.id} className="hover:bg-slate-800/50 border-b border-slate-700">
                                                    <td className="font-mono text-slate-400">{formatDate(permit)}</td>
                                                    <td className="font-bold text-white">{permit.task || permit.taskName}</td>
                                                    <td>{permit.executor || permit.monteur1 || '-'}</td>
                                                    <td>
                                                        {permit.conditionScore
                                                            ? <span className={`badge badge-sm ${permit.conditionScore <= 2 ? 'badge-success' : permit.conditionScore <= 4 ? 'badge-warning' : 'badge-error'}`}>NEN {permit.conditionScore}</span>
                                                            : <span className="text-slate-600">-</span>
                                                        }
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* SECTION: AI Kennis-Kluis */}
                    {dashboardTab === 'ai' && (
                        <div className="animate-fade-in">
                            <AssetKennisKluis
                                assetId={assetId}
                                instanceName={instanceName}
                                currentUser={currentUser}
                            />
                        </div>
                    )}

                    {/* MODAL: Execution View */}
                    {selectedPermit && (
                        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
                            <div className="bg-slate-900 w-full max-w-4xl max-h-[90vh] rounded-lg overflow-hidden shadow-2xl border border-slate-700 flex flex-col relative animate-fade-in">
                                <ExecutionView
                                    permit={enrichPermitWithLinks(selectedPermit, hierarchyData)}
                                    onClose={() => setSelectedPermit(null)}
                                    currentUser={currentUser}
                                    previewMode={true}
                                    embedded={true}
                                    users={users}
                                    allowAssignment={true}
                                    allowUpload={false}
                                />

                                {/* Close Button (X) - Rendered AFTER content to ensure visibility */}
                                <button
                                    onClick={() => setSelectedPermit(null)}
                                    style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 9999 }}
                                    className="absolute top-2 right-2 z-[100] bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg transition-transform hover:scale-105 font-bold"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    )}

                    {/* SECTION 2: Documentation (SOPs) */}

                    {/* GROUP 1: ACTIVE SOPs (Highlighted) */}
                    {activeSops.length > 0 && (
                        <div className="mb-4">
                            <div className="text-[10px] font-bold text-green-400 uppercase mb-2 px-1 flex items-center gap-2 border-b border-green-500/30 pb-1">
                                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                Actieve Werkinstructies
                            </div>
                            <div className="space-y-2 border-l-2 border-green-500/30 pl-2">
                                {activeSops.map((sop, idx) => (
                                    <a
                                        key={`active-sop-${idx}`}
                                        href={sop.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 bg-gradient-to-r from-green-900/20 to-slate-800 border border-green-700/30 rounded hover:bg-slate-700 transition-all group shadow-lg shadow-green-900/10 no-underline"
                                    >
                                        <span className="text-xl w-8 text-center">📋</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold truncate" style={{ color: '#4ade80' }}>{sop.name}</div>
                                            <div className="text-[10px]" style={{ color: '#86efac' }}>Instructie voor: <span className="font-bold">{sop.taskName}</span></div>
                                        </div>
                                        <span className="text-green-500">↗</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* GROUP 3: COMPONENT LIBRARY DOCS (Technical, Electrical, Manual) */}
                    {(() => {
                        const compMeta = finalCompMeta;
                        const hasCompDocs = compMeta.docLink || compMeta.docLinkElectro || compMeta.docLinkManual;
                        if (!hasCompDocs) return null;

                        return (
                            <div className="mt-4">
                                <div className="text-[10px] font-bold text-blue-400 uppercase mb-2 px-1 border-b border-blue-500/30 pb-1 flex items-center gap-2">
                                    <span>⚙️</span> Onderdeel Documentatie
                                </div>
                                <div className="grid grid-cols-1 gap-2">
                                    {compMeta.docLink && (
                                        <a href={compMeta.docLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 bg-slate-800/50 border border-slate-700/50 rounded hover:bg-slate-700 transition-all group no-underline">
                                            <span className="text-lg">📐</span>
                                            <div className="flex-1 text-xs font-bold text-slate-300 group-hover:text-white">Technische Documentatie</div>
                                            <span className="text-slate-500 text-xs">↗</span>
                                        </a>
                                    )}
                                    {compMeta.docLinkElectro && (
                                        <a href={compMeta.docLinkElectro} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 bg-slate-800/50 border border-slate-700/50 rounded hover:bg-slate-700 transition-all group no-underline">
                                            <span className="text-lg">⚡</span>
                                            <div className="flex-1 text-xs font-bold text-slate-300 group-hover:text-white">Elektrisch Schema</div>
                                            <span className="text-slate-500 text-xs">↗</span>
                                        </a>
                                    )}
                                    {compMeta.docLinkManual && (
                                        <a href={compMeta.docLinkManual} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 bg-slate-800/50 border border-slate-700/50 rounded hover:bg-slate-700 transition-all group no-underline">
                                            <span className="text-lg">📘</span>
                                            <div className="flex-1 text-xs font-bold text-slate-300 group-hover:text-white">Handleiding / Manual</div>
                                            <span className="text-slate-500 text-xs">↗</span>
                                        </a>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* EMPTY STATE */}
                    {(() => {
                        const hasActiveSops = activeSops.length > 0;
                        const hasLibrarySops = librarySops.length > 0;
                        const compMeta = finalCompMeta;
                        const hasCompDocs = compMeta.docLink || compMeta.docLinkElectro || compMeta.docLinkManual;

                        // Check if any subline docs exist
                        let hasSublineDocs = false;
                        if (deepLinkLocation && hierarchyData) {
                            const subObj = hierarchyData?.[deepLinkLocation.hall]?.[deepLinkLocation.line]?.[deepLinkLocation.subline];
                            if (subObj) {
                                hasSublineDocs = !!(subObj.docLink || subObj.docLinkElectro || subObj.docLinkManual);
                            }
                        }

                        if (!hasActiveSops && !hasLibrarySops && !hasSublineDocs && !hasCompDocs) {
                            return (
                                <div className="text-center p-4 bg-slate-800/50 rounded text-slate-500 text-xs italic">
                                    Geen specifieke documentatie gekoppeld.
                                </div>
                            );
                        }
                        return null;
                    })()}
                </div>
            </div>
        </div>
    );
};

export default AssetDashboard;
